import {useMemo, useState} from "react";
import {Card, Col, Empty, Row, Table, Tooltip, Typography} from "antd";
import moment from "moment";
import {t} from "i18next";
import ApiService from "../../../core/service/api-service";
import {CountBarChart, DailyBarChart, dailySummary, defaultPanelFilter, InfoTitle, modalitySlot, PanelRangeBar, PanelSpin, percent, studyTypeLabel, Tile, usePanelData} from "./PanelParts";
import {info} from "./info";

const fetchOverview = (filter) => ApiService.Dashboard.AdminOverview(filter)
const fetchQuality = (filter) => ApiService.Dashboard.AdminQuality(filter)

const numberSorter = (key) => (a, b) => (a[key] ?? 0) - (b[key] ?? 0)
const textSorter = (key) => (a, b) => (a[key] || '').localeCompare(b[key] || '', 'tr')
const tablePagination = {pageSize: 20, hideOnSinglePage: true, showSizeChanger: false}

/**
 * Yönetim panoları Dashboard sayfasında İstatistikler/Panom ile aynı düzeyde ayrı sekmeler.
 * Her sekmenin kendi tarih çubuğu ve süzgeci var; sekme ilk açıldığında yüklenir
 * (antd Tabs pasif sekmeyi baştan basmaz, açıldıktan sonra da durumunu korur).
 */
export const adminTabItems = () => [
    {key: 'admin-overview', label: t('Overview'), children: <WithRange Section={OverviewSection}/>},
]

function WithRange({Section}) {
    const [filter, setFilter] = useState(defaultPanelFilter)

    return (
        <>
            <PanelRangeBar onSearch={setFilter} withHospital/>
            <Section filter={filter}/>
        </>
    )
}

// ---------------------------------------------------------------------------------------------
// Genel bakış: gelen çekim + durumu + dağılım, altında kalite. Kalite ayrı uçtan gelir (ağır sorgu),
// kendi spinner'ıyla yüklenir; üst kısım onu beklemez. Hastane süzgeci ikisini de daraltır.
// ---------------------------------------------------------------------------------------------

// Tamamlanma süresi kartlarının sırası: Acil, Rutin, Yatan.
const COMPLETION_TYPES = [3, 1, 2]

function OverviewSection({filter}) {
    const {data, loading} = usePanelData(fetchOverview, filter)
    const summary = dailySummary(data?.daily)

    const delayedHint = (data?.delayedByStudyType || [])
        .filter(x => x.count > 0)
        .map(x => `${studyTypeLabel(x.id)} ${x.count}`)
        .join(' · ')

    return (
        <>
            <PanelSpin loading={loading}>
                <Row gutter={[12, 12]}>
                    <Col flex={'1 1 160px'}>
                        <Tile title={t('Incoming studies')} info={info('incomingStudies')} value={data?.studyCount}/>
                    </Col>
                    <Col flex={'1 1 160px'}>
                        <Tile title={t('Unread studies')} info={info('unreadStudies')} value={data?.unreadCount}
                              hint={percent(data?.unreadCount, data?.studyCount)}/>
                    </Col>
                    <Col flex={'1 1 160px'}>
                        <Tile title={t('Unwritten requests')} info={info('unwritten')} value={data?.unwrittenCount}
                              hint={percent(data?.unwrittenCount, data?.requestCount)}/>
                    </Col>
                    <Col flex={'1 1 160px'}>
                        <Tile title={t('Daily average')} info={info('dailyAverage')} value={summary.average}/>
                    </Col>
                    <Col flex={'1 1 160px'}>
                        <Tile
                            title={t('Busiest day')}
                            info={info('busiestDay')}
                            value={summary.busiest?.count}
                            hint={summary.busiest ? moment(summary.busiest.day).format('DD.MM.YYYY dddd') : null}
                        />
                    </Col>

                    <Col flex={'1 1 160px'}>
                        <Tile title={t('Delayed studies')} info={info('delayed')} value={data?.delayedCount}
                              hint={delayedHint || null}/>
                    </Col>
                    {COMPLETION_TYPES.map(type => {
                        const item = data?.completion?.find(x => x.studyType === type)
                        return (
                            <Col key={type} flex={'1 1 160px'}>
                                <Tile
                                    title={`${t('Avg. completion')} · ${studyTypeLabel(type)}`}
                                    info={info('completion')}
                                    value={item ? Math.round(item.averageMinutes) : '-'}
                                    suffix={item ? t('minute short') : null}
                                    hint={item ? `${item.reportCount} ${t('reports')}` : t('No data')}
                                />
                            </Col>
                        )
                    })}

                    <Col xs={24}>
                        <DailyBarChart title={t('Incoming studies by day')} info={info('incomingByDay')} daily={data?.daily}/>
                    </Col>

                    <Col xs={24} xl={14}>
                        <Heatmap cells={data?.heatmap}/>
                    </Col>
                    <Col xs={24} xl={10}>
                        <CountBarChart title={t('By modality')} info={info('incomingByModality')} items={data?.modalities} slotOf={modalitySlot}/>
                    </Col>

                    <Col xs={24}>
                        <HospitalTable rows={data?.hospitals} days={data?.daily?.length}/>
                    </Col>
                </Row>
            </PanelSpin>

            <Typography.Title level={5} style={{marginTop: 24}}>{t('Quality')}</Typography.Title>
            <QualitySection filter={filter}/>
        </>
    )
}

/** Sayı + altında gri yüzde (Tile ipucuyla aynı biçim). */
const countWithRate = (value, whole) => (
    <>
        {value}
        {value > 0 && <div style={{color: 'var(--text-3)', fontSize: 11}}>{percent(value, whole)}</div>}
    </>
)

const completionOf = (row, type) => row.completion?.find(x => x.studyType === type)

/**
 * Kartlardaki sayımların hastane kırılımı: hangi hastanede gecikme/birikme var tek bakışta görülsün.
 * Satırlar sunucudan; günlük ortalama kartla aynı tanım (toplam / aralıktaki gün).
 */
function HospitalTable({rows, days}) {
    const dailyAverage = (r) => days ? Math.round((r.studyCount / days) * 10) / 10 : 0

    const columns = [
        {title: t('Hospital'), dataIndex: 'name', fixed: 'left', sorter: textSorter('name'), render: v => v || '-'},
        {title: t('Incoming studies'), dataIndex: 'studyCount', align: 'right', sorter: numberSorter('studyCount'), defaultSortOrder: 'descend'},
        {
            title: t('Unread studies'), dataIndex: 'unreadCount', align: 'right', sorter: numberSorter('unreadCount'),
            render: (v, r) => countWithRate(v, r.studyCount),
        },
        {
            title: t('Unwritten requests'), dataIndex: 'unwrittenCount', align: 'right', sorter: numberSorter('unwrittenCount'),
            render: (v, r) => countWithRate(v, r.requestCount),
        },
        {
            title: t('Daily average'), key: 'dailyAverage', align: 'right',
            sorter: (a, b) => dailyAverage(a) - dailyAverage(b),
            render: (_, r) => dailyAverage(r),
        },
        {
            title: t('Delayed studies'), dataIndex: 'delayedCount', align: 'right', sorter: numberSorter('delayedCount'),
            render: (v, r) => {
                const hint = (r.delayedByStudyType || [])
                    .filter(x => x.count > 0)
                    .map(x => `${studyTypeLabel(x.id)} ${x.count}`)
                    .join(' · ')
                return hint ? <Tooltip title={hint}><span style={{cursor: 'help'}}>{v}</span></Tooltip> : v
            },
        },
        ...COMPLETION_TYPES.map(type => ({
            title: `${t('Avg. completion')} · ${studyTypeLabel(type)}`,
            key: `completion-${type}`,
            align: 'right',
            sorter: (a, b) => (completionOf(a, type)?.averageMinutes ?? -1) - (completionOf(b, type)?.averageMinutes ?? -1),
            render: (_, r) => {
                const item = completionOf(r, type)
                if (!item)
                    return '-'
                return (
                    <Tooltip title={`${item.reportCount} ${t('reports')}`}>
                        <span style={{cursor: 'help'}}>{Math.round(item.averageMinutes)} {t('minute short')}</span>
                    </Tooltip>
                )
            },
        })),
    ]

    return (
        <Card size={'small'} title={<InfoTitle title={t('By hospital')} info={info('byHospital')}/>}>
            {rows?.length ? (
                <Table
                    size={'small'}
                    rowKey={'hospitalId'}
                    columns={columns}
                    dataSource={rows}
                    pagination={tablePagination}
                    scroll={{x: true}}
                />
            ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No data')}/>}
        </Card>
    )
}

// Pazartesi başlı hafta; sunucu 0 = Pazar gönderir.
const WEEK_ORDER = [1, 2, 3, 4, 5, 6, 0]
const HOURS = Array.from({length: 24}, (_, i) => i)

/** Gün × saat yoğunluğu. Tek ton: hücre --chart-1'in saydamlığıyla koyulaşır, 0 boş zemin kalır. */
function Heatmap({cells}) {
    const {matrix, max} = useMemo(() => {
        const m = {}
        let mx = 0
        ;(cells || []).forEach(c => {
            m[`${c.dayOfWeek}-${c.hour}`] = c.count
            mx = Math.max(mx, c.count)
        })
        return {matrix: m, max: mx}
    }, [cells])

    const cellStyle = {height: 22, borderRadius: 3, position: 'relative', overflow: 'hidden', background: 'var(--surface-2)'}

    return (
        <Card size={'small'} title={<InfoTitle title={t('Arrival by weekday and hour')} info={info('arrivalHeatmap')}/>} style={{height: '100%'}}>
            {!max ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No data')}/> : (
                <div style={{overflowX: 'auto'}}>
                    <div style={{display: 'grid', gridTemplateColumns: '40px repeat(24, minmax(16px, 1fr))', gap: 2, minWidth: 460}}>
                        <div/>
                        {HOURS.map(h => (
                            <div key={h} style={{fontSize: 10, color: 'var(--text-3)', textAlign: 'center'}}>
                                {h % 3 === 0 ? String(h).padStart(2, '0') : ''}
                            </div>
                        ))}
                        {WEEK_ORDER.map(day => [
                            <div key={`l${day}`} style={{fontSize: 11, color: 'var(--text-2)', lineHeight: '22px'}}>
                                {moment().day(day).format('ddd')}
                            </div>,
                            ...HOURS.map(hour => {
                                const count = matrix[`${day}-${hour}`] || 0
                                return (
                                    <Tooltip
                                        key={`${day}-${hour}`}
                                        title={`${moment().day(day).format('dddd')} ${String(hour).padStart(2, '0')}:00–${String(hour).padStart(2, '0')}:59 · ${count}`}
                                    >
                                        <div style={cellStyle}>
                                            {count > 0 && (
                                                <div style={{
                                                    position: 'absolute', inset: 0, background: 'var(--chart-1)',
                                                    opacity: 0.12 + 0.88 * (count / max)
                                                }}/>
                                            )}
                                        </div>
                                    </Tooltip>
                                )
                            })
                        ])}
                    </div>
                    <div style={{display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 11, color: 'var(--text-3)'}}>
                        <span>0</span>
                        <div style={{width: 120, height: 8, borderRadius: 4, background: 'linear-gradient(90deg, var(--surface-2), var(--chart-1))'}}/>
                        <span>{max}</span>
                    </div>
                </div>
            )}
        </Card>
    )
}

// ---------------------------------------------------------------------------------------------
// Kalite
// ---------------------------------------------------------------------------------------------

function QualitySection({filter}) {
    const {data, loading} = usePanelData(fetchQuality, filter)
    const re = data?.reAssessment
    const closed = (re?.changed || 0) + (re?.unchanged || 0)

    const reColumns = [
        {title: t('Doctor'), dataIndex: 'name', sorter: textSorter('name'), render: v => v || t('No doctor assigned')},
        {title: t('Total'), dataIndex: 'total', align: 'right', sorter: numberSorter('total')},
        {title: t('Report changed'), dataIndex: 'changed', align: 'right', sorter: numberSorter('changed'), defaultSortOrder: 'descend'},
        {title: t('Report unchanged'), dataIndex: 'unchanged', align: 'right', sorter: numberSorter('unchanged')},
        {title: t('Still open'), dataIndex: 'open', align: 'right', sorter: numberSorter('open')},
        {
            title: t('Change rate'), key: 'rate', align: 'right',
            sorter: (a, b) => rateOf(a.changed, a.changed + a.unchanged) - rateOf(b.changed, b.changed + b.unchanged),
            render: (_, r) => (r.changed + r.unchanged) ? percent(r.changed, r.changed + r.unchanged) : '-',
        },
    ]

    const editedColumns = [
        {title: t('Reporter'), dataIndex: 'name', sorter: textSorter('name'), render: v => v || '-'},
        {title: t('Written reports'), dataIndex: 'total', align: 'right', sorter: numberSorter('total'), defaultSortOrder: 'descend'},
        {title: t('Edited by someone else'), dataIndex: 'editedByOthers', align: 'right', sorter: numberSorter('editedByOthers')},
        {
            title: t('Rate'), key: 'rate', align: 'right',
            sorter: (a, b) => rateOf(a.editedByOthers, a.total) - rateOf(b.editedByOthers, b.total),
            render: (_, r) => percent(r.editedByOthers, r.total),
        },
    ]

    return (
        <PanelSpin loading={loading}>
            <Row gutter={[12, 12]}>
                <Col xs={12} md={8} xl={4}><Tile title={t('Re-assessment requests')} info={info('qReAssessment')} value={re?.total}/></Col>
                <Col xs={12} md={8} xl={4}><Tile title={t('Report changed')} info={info('qReChanged')} value={re?.changed} hint={closed ? percent(re?.changed, closed) : null}/></Col>
                <Col xs={12} md={8} xl={4}><Tile title={t('Report unchanged')} info={info('reUnchanged')} value={re?.unchanged}/></Col>
                <Col xs={12} md={8} xl={4}><Tile title={t('Still open')} info={info('reOpen')} value={re?.open}/></Col>
                <Col xs={12} md={8} xl={4}><Tile title={t('Confirmation removal requests')} info={info('removeConfirmation')} value={data?.removeConfirmationCount}/></Col>
                <Col xs={12} md={8} xl={4}><Tile title={t('Missing image requests')} info={info('missingImage')} value={data?.missingImageCount}/></Col>

                <Col xs={24} xl={12}>
                    <Card size={'small'} title={<InfoTitle title={t('Re-assessment by doctor')} info={info('reByDoctor')}/>}>
                        {data?.reAssessmentByDoctor?.length ? (
                            <Table
                                size={'small'}
                                rowKey={r => r.userId || 'none'}
                                columns={reColumns}
                                dataSource={data.reAssessmentByDoctor}
                                pagination={tablePagination}
                                scroll={{x: true}}
                            />
                        ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No data')}/>}
                    </Card>
                </Col>
                <Col xs={24} xl={12}>
                    <Card size={'small'} title={<InfoTitle title={t('Reports edited by someone else')} info={info('editedByOthers')}/>}>
                        {data?.reportersEdited?.length ? (
                            <Table
                                size={'small'}
                                rowKey={r => r.userId || 'none'}
                                columns={editedColumns}
                                dataSource={data.reportersEdited}
                                pagination={tablePagination}
                                scroll={{x: true}}
                            />
                        ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('No data')}/>}
                    </Card>
                </Col>
            </Row>
        </PanelSpin>
    )
}

const rateOf = (part, whole) => whole ? part / whole : 0
